import { LocalPost } from "./types";
import { tryApiPaths } from "./httpClient";

// ==================== Posts API ====================
// Usa /api/businesses/{publicId}/posts (nova API com publicId direto)

// Helper para extrair array de resposta em vários formatos possíveis
function extractArray(response: unknown): LocalPost[] {
  if (Array.isArray(response)) return response;
  const data = response as {
    posts?: unknown;
    data?: unknown;
    result?: unknown;
  };
  if (Array.isArray(data.posts)) return data.posts as LocalPost[];
  if (Array.isArray(data.data)) return data.data as LocalPost[];
  if (Array.isArray(data.result)) return data.result as LocalPost[];
  return [];
}

// Helper para extrair objeto de resposta
function extractObject(response: unknown): Record<string, unknown> {
  if (response && typeof response === "object" && !Array.isArray(response)) {
    return response as Record<string, unknown>;
  }
  const data = response as { post?: unknown; data?: unknown };
  if (data.post && typeof data.post === "object") return data.post as Record<string, unknown>;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data as Record<string, unknown>;
  }
  return {};
}

// Helper para remover campos vazios de objetos aninhados
function cleanObject(obj: unknown): unknown {
  if (typeof obj !== "object" || Array.isArray(obj) || obj === null) {
    return obj;
  }
  
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip empty key names
    if (key === "") {
      continue;
    }
    
    if (value === undefined || value === null || value === "") {
      continue;
    }
    
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    
    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = cleanObject(value);
      if (
        nested &&
        typeof nested === "object" &&
        !Array.isArray(nested) &&
        Object.keys(nested).length > 0
      ) {
        cleaned[key] = nested;
      }
      continue;
    }
    
    cleaned[key] = value;
  }
  
  return cleaned;
}

// Helper para limpar objeto antes de enviar (usa whitelist rigorosa)
function sanitizePost(post: Partial<LocalPost>): Record<string, unknown> {
  // Whitelist de campos permitidos para criar/atualizar um post
  const allowedFields = [
    "summary",           // Descrição do post (required)
    "topicType",        // Tipo do post: STANDARD, GOOGLE_UPDATED, PRODUCT, EVENT, OFFER, ALERT, NEWS (required)
    "languageCode",     // Código de idioma (required)
    "media",            // Array de mídia
    "customUrl",        // URL customizada
    "callToActionType", // Tipo de call to action
    "callToActionUrl",  // URL do call to action
    "event",            // Detalhes de evento
    "offer",            // Detalhes de oferta
    "alert",            // Detalhes de alerta
    "product",          // Detalhes de produto
    "postId",           // ID do post (pode estar presente)
    "name",             // Nome interno do post
  ];
  
  console.log("[gbpPostsApi.sanitizePost] raw input", post);
  
  // Detectar campos desconhecidos ou nomes vazios
  const unknownFields: string[] = [];
  const emptyNameFields: string[] = [];
  
  for (const key of Object.keys(post)) {
    if (key === "") {
      emptyNameFields.push(key);
    } else if (!allowedFields.includes(key)) {
      unknownFields.push(key);
    }
  }
  
  if (emptyNameFields.length > 0) {
    console.warn("[gbpPostsApi.sanitizePost] FOUND EMPTY NAME FIELDS! THIS IS LIKELY THE BUG:", emptyNameFields, post);
  }
  
  if (unknownFields.length > 0) {
    console.warn("[gbpPostsApi.sanitizePost] Found unknown fields (will be stripped):", unknownFields);
  }
  
  const sanitized: Record<string, unknown> = {};
  
  for (const field of allowedFields) {
    const value = post[field as keyof typeof post];
    
    // Skip undefined, null, empty strings
    if (value === undefined || value === null || value === "") {
      continue;
    }
    
    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    
    // Para objetos, fazer deep clean
    if (typeof value === "object" && !Array.isArray(value)) {
      const cleaned = cleanObject(value);
      if (
        cleaned &&
        typeof cleaned === "object" &&
        !Array.isArray(cleaned) &&
        Object.keys(cleaned).length > 0
      ) {
        sanitized[field] = cleaned;
      }
      continue;
    }
    
    // Incluir o valor
    sanitized[field] = value;
  }
  
  console.log("[gbpPostsApi.sanitizePost] sanitized output", sanitized);
  
  return sanitized;
}

export const gbpPostsApi = {
  /**
   * Lista todas as postagens de um negócio.
   * Tenta múltiplos paths: /api/businesses/{publicId}/posts
   */
  async listPosts(publicId: string): Promise<LocalPost[]> {
    try {
      const r = await tryApiPaths(
        "get",
        [
          `/api/businesses/${publicId}/posts`,
          `/businesses/${publicId}/posts`,
        ],
        undefined,
        { gbpOnly: true }
      );
      console.log("[gbpPostsApi.listPosts]", "success", publicId, r);
      return extractArray(r?.data || r);
    } catch (err) {
      console.error("[gbpPostsApi.listPosts]", "error", err);
      return [];
    }
  },

  /**
   * Busca uma postagem específica.
   * GET /api/businesses/{publicId}/posts/{postId}
   */
  async getPost(publicId: string, postId: string): Promise<LocalPost> {
    try {
      const r = await tryApiPaths(
        "get",
        [
          `/api/businesses/${publicId}/posts/${postId}`,
          `/businesses/${publicId}/posts/${postId}`,
        ],
        undefined,
        { gbpOnly: true }
      );
      return extractObject(r?.data || r) as unknown as LocalPost;
    } catch (err) {
      console.error("[gbpPostsApi.getPost]", "error", err);
      return {} as LocalPost;
    }
  },

  /**
   * Cria uma nova postagem.
   * POST /api/businesses/{publicId}/posts
   */
  async createPost(publicId: string, post: LocalPost): Promise<LocalPost> {
    try {
      const sanitized = sanitizePost(post);
      console.log("[gbpPostsApi.createPost]", "sending", sanitized);
      const r = await tryApiPaths(
        "post",
        [
          `/api/businesses/${publicId}/posts`,
          `/businesses/${publicId}/posts`,
        ],
        sanitized,
        { gbpOnly: true }
      );
      console.log("[gbpPostsApi.createPost]", "success", publicId, r);
      return extractObject(r?.data || r) as unknown as LocalPost;
    } catch (err) {
      console.error("[gbpPostsApi.createPost]", "error", err);
      throw err;
    }
  },

  /**
   * Atualiza uma postagem existente.
   * PATCH /api/businesses/{publicId}/posts/{postId}
   */
  async updatePost(publicId: string, postId: string, post: Partial<LocalPost>): Promise<LocalPost> {
    try {
      const sanitized = sanitizePost(post);
      console.log("[gbpPostsApi.updatePost]", "sending", sanitized);
      const r = await tryApiPaths(
        "patch",
        [
          `/api/businesses/${publicId}/posts/${postId}`,
          `/businesses/${publicId}/posts/${postId}`,
        ],
        sanitized,
        { gbpOnly: true }
      );
      console.log("[gbpPostsApi.updatePost]", "success", publicId, postId, r);
      return extractObject(r?.data || r) as unknown as LocalPost;
    } catch (err) {
      console.error("[gbpPostsApi.updatePost]", "error", err);
      throw err;
    }
  },

  /**
   * Deleta uma postagem.
   * DELETE /api/businesses/{publicId}/posts/{postId}
   */
  async deletePost(publicId: string, postId: string): Promise<void> {
    try {
      await tryApiPaths(
        "delete",
        [
          `/api/businesses/${publicId}/posts/${postId}`,
          `/businesses/${publicId}/posts/${postId}`,
        ],
        undefined,
        { gbpOnly: true }
      );
      console.log("[gbpPostsApi.deletePost]", "success", publicId, postId);
    } catch (err) {
      console.error("[gbpPostsApi.deletePost]", "error", err);
      throw err;
    }
  },
};
